import { useState } from 'react';
import dynamic from 'next/dynamic';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Api Endpoints
import { ACTIVATE_SOCIAL_PROFILE } from '@/lib/routes/apiEndpoints';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Checkbox from '@/components/base/Checkbox';
import Icon from '@/components/base/Icon';
import Switch from '@/components/base/Switch';
import { GroupList } from '@/components/pages/socialprofiles/GroupList';

const CantDeleteModal = dynamic(() =>
    import('@/components/pages/socialprofiles/modals').then((mod) => mod.CantDeleteModal)
);

const CantSwitchModal = dynamic(() =>
    import('@/components/pages/socialprofiles/modals').then((mod) => mod.CantSwitchModal)
);

const WarningDeactivateSPModal = dynamic(() =>
    import('@/components/pages/socialprofiles/modals').then((mod) => mod.WarningDeactivateSPModal)
);

const DeleteSocialProfileModal = dynamic(
    () => import('@/components/pages/socialprofiles/DeleteSocialProfileModal')
);

// Hooks
import useAcl from '@/hooks/user/useAcl';
import { useUserGroupRestriction } from '@/utils/hooks/acl/useUserGroupRestriction';
import useGetSocialProfileStyle from '@/utils/hooks/socialprofiles/useGetSocialProfileStyle/useGetSocialProfileStyle';
import type { MutateSocialProfilesType } from '@/utils/hooks/swr/useSocialProfiles';
import useUser from '@/utils/hooks/swr/useUser';
import useUserRole from '@/utils/hooks/user/useUserRole';

import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useToggle from '@/utils/hooks/utils/useToggle';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { STATUS } from '@/lib/socialprofiles/status';

//type
import type { DisplayedSocialProfileType } from '@/components/pages/socialprofiles/SocialProfilesList';

const SOCIAL_NETWORK_TYPE_LOCALES = {
    facebook: 'facebook_page',
    instagram: 'instagram_profile',
    x: 'x_profile',
    tiktok_business: 'tiktok_profile',
    linkedin: 'linkedin_page',
    youtube: 'youtube_profile',
    pinterest: 'pinterest_profile',
    threads: 'threads_profile'
} as const;

const requiresAuthActionList = [
    STATUS.reAuth,
    STATUS.reAuthSchedulerPerms,
    STATUS.userTeamSchedulerPerms,
    STATUS.refresh
] as const;

type SocialProfileProps = React.ComponentPropsWithoutRef<'div'> & {
    socialProfile: DisplayedSocialProfileType;
    isOnTrial: boolean;
    cantDelete: boolean;
    mutateSocialProfiles: MutateSocialProfilesType;
    addSocialProfile: () => void;
    handleSelectSocialProfile: (socialProfile: DisplayedSocialProfileType) => void;
    updateSocialProfile: (socialProfile: DisplayedSocialProfileType) => Promise<void>;
    toggleGroupModal: (socialProfile: DisplayedSocialProfileType) => void;
    manageProfilesAdded: (socialProfiles: DisplayedSocialProfileType[]) => void;
    isSelected: boolean;
};

const SocialProfile = ({
    socialProfile,
    isOnTrial,
    cantDelete,
    mutateSocialProfiles,
    addSocialProfile,
    handleSelectSocialProfile,
    updateSocialProfile,
    manageProfilesAdded,
    toggleGroupModal,
    isSelected,
    ...restProps
}: SocialProfileProps) => {
    const { t } = useTranslation('socialprofiles');
    const { user } = useUser();
    const { hasAcl } = useAcl();
    const { isTeamMember } = useUserRole();
    const { hasGroupPermission } = useUserGroupRestriction();
    const hasAccess = hasAcl(aclFeatures.social_profile.base.delete);

    const [formState, setFormState] = useState<{ isLoading: boolean; error?: string }>({
        isLoading: false,
        error: null
    });
    const { isOpen: isModalDeleteSocialProfileOpen, toggle: toggleModalDeleteSocialProfile } =
        useToggle();
    const { isOpen: isModalDeactivateSocialProfile, toggle: toggleModalDeactivateSocialProfile } =
        useToggle();
    const { isOpen: isCantDeleteOpen, toggle: toggleCantDeleteModal } = useToggle();
    const { isOpen: isCantSwitchOpen, toggle: toggleCantSwitchModal } = useToggle();

    const { external_api_data, social_network_type, slug, active } = socialProfile;

    const activateSocialProfile = async () => {
        if (user?.quotas?.profiles?.remaining === 0) {
            // Can't activate a profile if no more slots left in the user's plan
            return toggleCantSwitchModal();
        }

        setFormState({ isLoading: true, error: null });
        try {
            await post(
                injectEndpointUrlParams(ACTIVATE_SOCIAL_PROFILE, { SPID: socialProfile.id })
            );

            await updateSocialProfile(socialProfile);
            manageProfilesAdded([socialProfile]);
            setFormState({ isLoading: false, error: null });
        } catch (e) {
            setFormState({ isLoading: false, error: t('errors:error_occured_submit_data') });
        }
    };

    const deactivateSocialProfile = () => {
        if (cantDelete) {
            // Can't switch off and delete the last active profile
            return toggleCantDeleteModal();
        }

        toggleModalDeactivateSocialProfile();
    };

    const { borderColor, Label, StatusText } = useGetSocialProfileStyle({
        socialProfile,
        onDelete: toggleModalDeleteSocialProfile
    });

    const hasGroupActions = socialProfile.displayedStatus !== STATUS.inactive;

    // Profiles that have an auth CTA in the Label (should not be hidden on hover)
    const requiresAuthAction = requiresAuthActionList.includes(
        socialProfile.displayedStatus as (typeof requiresAuthActionList)[number]
    );

    return (
        <div
            data-testid={`social-profile-${socialProfile.id}`}
            className={clsx(
                'group flex flex-col items-center rounded-md border bg-white @container/sp dark:bg-icoDarkMode-deepBlack',
                borderColor
            )}
            {...restProps}>
            <div className="relative flex w-full flex-1 flex-wrap items-center px-8 py-5 @lg/sp:flex-nowrap">
                <div className="flex basis-1/4 flex-col items-start @lg/sp:basis-0 @lg/sp:items-center">
                    {isOnTrial && active ? (
                        <p className="mb-2 rounded bg-icoGreen-100 p-1 text-xs capitalize text-icoGreen dark:bg-icoGreen/20">
                            {t('trial')}
                        </p>
                    ) : null}
                    <Switch.Wrapper
                        checked={active}
                        disabled={isTeamMember || formState.isLoading || !hasAccess}>
                        <Switch
                            data-testid={`toggle-social-profile-${socialProfile.id}`}
                            onChange={active ? deactivateSocialProfile : activateSocialProfile}
                            ariaLabel={`${t('toggle_social_profile')} ${socialProfile.id}`}
                        />
                    </Switch.Wrapper>
                </div>
                <Label
                    className={clsx(
                        'flex flex-1 basis-3/4 items-center justify-end text-xs @lg/sp:order-last @lg/sp:basis-0',
                        {
                            'group-hover:hidden':
                                hasGroupActions && !isSelected && !requiresAuthAction,
                            hidden: isSelected
                        }
                    )}
                />
                <Avatar
                    className="mr-3 mt-8 @lg/sp:ml-9 @lg/sp:mt-0"
                    fallback={slug?.[0]}
                    url={external_api_data.avatar}
                    profileType={social_network_type}
                />
                <div className="mt-4 flex flex-col overflow-hidden @lg/sp:mt-0">
                    <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm">
                        {slug}
                    </p>
                    <p className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                        {external_api_data.brand_name ?? external_api_data.name}
                    </p>
                    <p className="mr-auto mt-1 flex-shrink truncate rounded bg-icoGray-100 p-2 text-9 tracking-1 text-icoGray-500 dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-wolf">
                        {t(
                            `commons:${SOCIAL_NETWORK_TYPE_LOCALES[social_network_type]}`
                        ).toUpperCase()}
                    </p>
                </div>
                <div
                    className={clsx(
                        'absolute right-0 h-full w-2/3 flex-1 items-center justify-end gap-x-10 rounded-tr-md bg-gradient-to-r from-white/0 from-10% via-white/70 via-15% to-white px-8 dark:from-icoDarkMode-deepBlack/0 dark:from-10% dark:via-icoDarkMode-deepBlack/70 dark:via-15% dark:to-icoDarkMode-deepBlack',
                        {
                            'group-hover:flex':
                                hasGroupActions && !isSelected && !requiresAuthAction,
                            flex: isSelected,
                            hidden: !isSelected
                        }
                    )}>
                    {!isSelected && hasGroupPermission ? (
                        <button
                            data-testid={`quick-add-to-group-${socialProfile.id}`}
                            onClick={() => toggleGroupModal(socialProfile)}
                            className="flex items-center space-x-1 rounded-full border border-icoBlue px-3 py-1 text-xxs text-icoBlue hover:bg-icoBlue-100 dark:hover:bg-icoBlue/20">
                            <Icon icon="plus" className="text-xs" />
                            <span>{t('groups.add_to_group')}</span>
                        </button>
                    ) : null}
                    <Checkbox
                        data-testid={`checkbox-${socialProfile.id}`}
                        checked={isSelected}
                        onChange={() => handleSelectSocialProfile(socialProfile)}
                    />
                </div>
            </div>
            {hasGroupPermission ? (
                <GroupList
                    groups={socialProfile?.groups}
                    maxDisplayed={4}
                    hasRoundedBottom={Boolean(
                        socialProfile.displayedStatus === 'active' ||
                            socialProfile.displayedStatus === 'disconnected'
                    )}
                />
            ) : null}
            {formState.error ? (
                <p
                    data-testid="error-message"
                    className="mb-2 text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {formState.error}
                </p>
            ) : null}
            <StatusText className="w-full rounded-b rounded-t-none px-8 py-3 text-sm" />

            {/* Modals */}
            <DeleteSocialProfileModal
                socialProfileId={socialProfile.id}
                isOpen={isModalDeleteSocialProfileOpen}
                toggle={toggleModalDeleteSocialProfile}
                onSuccess={mutateSocialProfiles}
            />
            <CantSwitchModal isOpen={isCantSwitchOpen} toggle={toggleCantSwitchModal} />
            <CantDeleteModal
                isOpen={isCantDeleteOpen}
                toggle={toggleCantDeleteModal}
                addSocialProfile={addSocialProfile}
                isOnTrial={isOnTrial}
            />
            <WarningDeactivateSPModal
                isOpen={isModalDeactivateSocialProfile}
                toggle={toggleModalDeactivateSocialProfile}
                socialProfile={socialProfile}
                updateSocialProfile={updateSocialProfile}
            />
        </div>
    );
};

export default SocialProfile;
